import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogIn, Trash2, KeyRound, ShieldCheck } from "lucide-react";
import { matrixApi, matrixError } from "@/lib/matrixApi";

const when = (iso) => (iso ? new Date(iso).toLocaleString("es-MX", {
  day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

/** Entrar al sitio analizado: usuario, contraseña y, si hace falta, código de 2 pasos. */
export const MatrixLogin = ({ onClose, onCount, onUse, activeSession }) => {
  const [form, setForm] = useState({ url: "", username: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);     // { pending_id }
  const [code, setCode] = useState("");
  const [shot, setShot] = useState("");
  const [note, setNote] = useState("");
  const [list, setList] = useState([]);

  const load = async () => {
    try {
      const { data } = await matrixApi.get("/sessions");
      setList(data.sessions);
      onCount?.(data.sessions.length);
    } catch (e) { toast.error(matrixError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const apply = (data) => {
    setShot(data.screenshot || "");
    setNote(data.message || "");
    if (data.status === "2fa" || data.status === "codigo_invalido") {
      setPending({ pending_id: data.pending_id });
      if (data.status === "codigo_invalido") toast.error(data.message);
      else toast.info(data.message);
      return;
    }
    setPending(null);
    if (data.status === "ok") {
      toast.success(data.message);
      setForm({ url: form.url, username: "", password: "" });
      setCode("");
      load();
      onUse?.(data.session_id);
    } else {
      toast.error(data.message);
    }
  };

  const start = async () => {
    if (!form.url.trim() || !form.username.trim() || !form.password) {
      return toast.error("Faltan la dirección, el usuario o la contraseña");
    }
    setBusy(true);
    setNote("");
    try {
      const { data } = await matrixApi.post("/login/start", { ...form, label: "" });
      apply(data);
    } catch (e) { toast.error(matrixError(e, "No pude entrar al sitio")); }
    finally { setBusy(false); }
  };

  const sendCode = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await matrixApi.post("/login/code",
        { pending_id: pending.pending_id, code });
      apply(data);
    } catch (e) { toast.error(matrixError(e)); setPending(null); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (pending) await matrixApi.post(`/login/cancel/${pending.pending_id}`).catch(() => {});
    setPending(null);
    setCode("");
  };

  const del = async (id) => {
    try {
      await matrixApi.delete(`/sessions/${id}`);
      if (activeSession === id) onUse?.("");
      load();
    } catch (e) { toast.error(matrixError(e)); }
  };

  const field = (key, placeholder, type = "text") => (
    <input type={type} value={form[key]} data-testid={`matrix-login-${key}`}
      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      onKeyDown={(e) => e.key === "Enter" && start()} placeholder={placeholder}
      className="w-full glass-card px-3 py-2 bg-black/50 text-white text-xs outline-none" />
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-5"
      data-testid="matrix-login-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="glass-card forge-card p-5 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <span className="font-heading text-white tracking-wider flex items-center gap-2">
            <LogIn className="w-4 h-4 text-red-400" /> ENTRAR AL SITIO
          </span>
          <button onClick={onClose} data-testid="matrix-login-close"
            className="text-white/50 hover:text-white">✕</button>
        </div>

        <p className="text-[11px] text-white/45 mb-3">
          Abro la página de acceso, escribo tus datos y, si la web pide un código de 2 pasos,
          te lo pregunto aquí mismo. La contraseña no se guarda en ningún lado: solo se
          conserva la sesión del navegador (7 días) para que el análisis entre conectado.
        </p>

        {!pending && (
          <div className="space-y-2">
            {field("url", "Dirección de la página de acceso (ejemplo.com/login)")}
            {field("username", "Usuario o correo")}
            {field("password", "Contraseña", "password")}
            <button data-testid="matrix-login-start" onClick={start} disabled={busy}
              className="matrix-btn px-5 py-2.5 rounded-xl text-xs flex items-center gap-2">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <LogIn className="w-3.5 h-3.5" />}
              {busy ? "Entrando…" : "Entrar"}
            </button>
          </div>
        )}

        {pending && (
          <div className="space-y-2" data-testid="matrix-login-2fa">
            <div className="text-xs text-amber-200 flex items-center gap-2">
              <KeyRound className="w-3.5 h-3.5" /> Escribe el código de verificación
            </div>
            <input value={code} onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendCode()} placeholder="123456"
              data-testid="matrix-login-code"
              className="w-full glass-card px-3 py-2 bg-black/50 text-white text-sm font-mono tracking-widest outline-none" />
            <div className="flex gap-2">
              <button data-testid="matrix-login-code-send" onClick={sendCode} disabled={busy}
                className="matrix-btn px-5 py-2.5 rounded-xl text-xs flex items-center gap-2">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <ShieldCheck className="w-3.5 h-3.5" />} Confirmar código
              </button>
              <button onClick={cancel} data-testid="matrix-login-cancel"
                className="glass-btn px-4 py-2.5 rounded-xl text-xs">Cancelar</button>
            </div>
            <p className="text-[10px] text-white/35">
              Tienes unos 7 minutos antes de que la ventana se cierre sola.
            </p>
          </div>
        )}

        {note && <p className="text-[11px] text-white/60 mt-3">{note}</p>}
        {shot && (
          <img src={shot} alt="Cómo quedó la pantalla" data-testid="matrix-login-shot"
            className="mt-3 w-full rounded-lg border border-white/10" />
        )}

        <div className="mt-5">
          <div className="text-[10px] text-white/40 uppercase tracking-wide mb-1">
            Sesiones guardadas
          </div>
          <div className="glass-card divide-y divide-white/5">
            {list.length === 0 && (
              <p className="text-xs text-white/40 p-4">Todavía no hay sesiones guardadas.</p>
            )}
            {list.map((s) => (
              <div key={s.session_id} className="px-3 py-2.5 flex items-center gap-3 text-xs"
                data-testid={`matrix-session-${s.session_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-white/85 truncate">{s.host}</div>
                  <div className="text-[10px] text-white/35">
                    {when(s.created_at)} · {s.cookies} cookies
                  </div>
                </div>
                <button onClick={() => { onUse?.(s.session_id); onClose(); }}
                  data-testid={`matrix-session-use-${s.session_id}`}
                  className={`px-2.5 py-1 rounded-lg text-[10px] ${
                    activeSession === s.session_id
                      ? "bg-red-600/70 text-white" : "glass-btn text-white/70"}`}>
                  {activeSession === s.session_id ? "En uso" : "Usar"}
                </button>
                <button onClick={() => del(s.session_id)}
                  data-testid={`matrix-session-del-${s.session_id}`}
                  className="text-white/50 hover:text-red-400 shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
